import SendIcon from '@mui/icons-material/Send';
import { Paper, Box, TextField, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * SECURITY: Maximum message length to prevent DoS attacks
 * Limits input size to prevent:
 * - Client-side DoS (very long strings)
 * - Backend payload size attacks
 * - Database storage issues
 */
const MAX_MESSAGE_LENGTH = 4000; // 4000 characters

interface ChatInputProps {
  input: string;
  loading: boolean;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  input,
  loading,
  onInputChange,
  onSend,
  onKeyPress
}) => {
  const { t } = useTranslation();
  const [lengthError, setLengthError] = useState<string | null>(null);

  // Update error state when input length changes
  useEffect(() => {
    if (input.length > MAX_MESSAGE_LENGTH) {
      setLengthError(
        t('chat.errors.messageTooLong', {
          current: input.length,
          max: MAX_MESSAGE_LENGTH,
          defaultValue: `Message too long (${input.length}/${MAX_MESSAGE_LENGTH} characters)`
        })
      );
    } else {
      setLengthError(null);
    }
  }, [input.length, t]);

  // SECURITY: Enforce length limit on input change
  const handleInputChange = (value: string): void => {
    // Allow typing up to max length
    if (value.length <= MAX_MESSAGE_LENGTH) {
      onInputChange(value);
    }
    // If over limit, truncate to max length
    else {
      onInputChange(value.substring(0, MAX_MESSAGE_LENGTH));
    }
  };

  // Calculate character count display
  const characterCount = input.length;
  const isNearLimit = characterCount > MAX_MESSAGE_LENGTH * 0.9; // 90% of limit

  // Helper text with character count
  const helperText = lengthError || (
    <span style={{ color: isNearLimit ? '#d32f2f' : '#666' }}>
      {characterCount}/{MAX_MESSAGE_LENGTH} {t('chat.characters', { defaultValue: 'characters' })}
    </span>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.08)',
        border: '1px solid #C9C9C9'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('chat.inputPlaceholder')}
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={onKeyPress}
            disabled={loading}
            error={!!lengthError}
            helperText={helperText}
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{
              maxLength: MAX_MESSAGE_LENGTH,
              'aria-label': t('chat.inputAriaLabel', { defaultValue: 'Message input' }),
            }}
            sx={{
              mr: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: lengthError ? '#d32f2f' : '#003064',
                },
                '& fieldset': {
                  borderColor: lengthError ? '#d32f2f' : '#C9C9C9',
                },
                '&:hover fieldset': {
                  borderColor: lengthError ? '#d32f2f' : '#003064',
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                color: '#333333',
              },
              '& .MuiFormHelperText-root': {
                fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={onSend}
            disabled={loading || input.trim() === '' || !!lengthError}
            aria-label={t('chat.sendButton', { defaultValue: 'Send message' })}
            sx={{
              minWidth: '50px',
              height: '50px',
              borderRadius: '4px',
              ml: 1,
              alignSelf: 'flex-start',
              backgroundColor: (loading || input.trim() === '' || !!lengthError) ? '#C9C9C9' : '#003064',
              '&:hover': {
                backgroundColor: (loading || input.trim() === '' || !!lengthError) ? '#C9C9C9' : '#00204d',
              },
              color: 'white',
            }}
          >
            <SendIcon />
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};